import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { MovementComponent } from '../components/MovementComponent';
import { EventBus } from '../../utils/EventBus';

/** Collision check function type */
export type CollisionCheckFn = (x: number, y: number) => boolean;

/** Trigger check function type */
export type TriggerCheckFn = (x: number, y: number, entityId: string) => void;

/**
 * MovementSystem - Applies velocity to entity positions
 * Handles physics-based movement and collision detection
 */
export class MovementSystem extends System {
  public readonly requiredComponents = ['transform', 'movement'];

  /** Map boundaries (world coordinates) */
  private mapBounds = {
    minX: 0,
    maxX: 20,
    minY: 0,
    maxY: 20,
  };

  /** Whether to enforce map boundaries */
  private enforceBounds = true;

  /** Custom collision check function (e.g., tilemap walkability) */
  private collisionCheck: CollisionCheckFn | null = null;

  /** Custom trigger check function (e.g., tilemap triggers) */
  private triggerCheck: TriggerCheckFn | null = null;

  public init(): void {
    // System initialized
  }

  /**
   * Set map boundaries for collision
   */
  public setMapBounds(minX: number, maxX: number, minY: number, maxY: number): void {
    this.mapBounds = { minX, maxX, minY, maxY };
  }

  /**
   * Enable or disable boundary enforcement
   */
  public setEnforceBounds(enforce: boolean): void {
    this.enforceBounds = enforce;
  }

  /**
   * Set custom collision check function
   * Function should return true if position is walkable, false if blocked
   */
  public setCollisionCheck(fn: CollisionCheckFn | null): void {
    this.collisionCheck = fn;
  }

  /**
   * Set custom trigger check function
   * Called when entity moves to check for trigger tiles
   */
  public setTriggerCheck(fn: TriggerCheckFn | null): void {
    this.triggerCheck = fn;
  }

  public update(entities: Entity[], delta: number): void {
    const deltaSeconds = delta / 1000;

    for (const entity of entities) {
      this.moveEntity(entity, deltaSeconds);
    }
  }

  /**
   * Apply velocity to entity position
   */
  private moveEntity(entity: Entity, deltaSeconds: number): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    const movement = entity.getComponent<MovementComponent>('movement');

    if (!transform || !movement || !movement.canMove) return;

    // Skip if no velocity
    if (movement.velocityX === 0 && movement.velocityY === 0) {
      movement.isMoving = false;
      return;
    }

    // Calculate new position
    let newX = transform.x + movement.velocityX * deltaSeconds;
    let newY = transform.y + movement.velocityY * deltaSeconds;

    // Check collision using custom function if available
    if (this.collisionCheck) {
      // Check if new position is walkable
      if (!this.collisionCheck(newX, newY)) {
        // Try sliding along X axis
        if (this.collisionCheck(newX, transform.y)) {
          newY = transform.y;
        }
        // Try sliding along Y axis
        else if (this.collisionCheck(transform.x, newY)) {
          newX = transform.x;
        }
        // Completely blocked
        else {
          newX = transform.x;
          newY = transform.y;
        }
      }
    } else if (this.enforceBounds) {
      // Fallback to simple boundary enforcement
      const padding = 0.5;
      newX = Math.max(this.mapBounds.minX + padding, Math.min(this.mapBounds.maxX - padding, newX));
      newY = Math.max(this.mapBounds.minY + padding, Math.min(this.mapBounds.maxY - padding, newY));
    }

    // Check if position actually changed
    const moved = newX !== transform.x || newY !== transform.y;

    // Update position
    transform.x = newX;
    transform.y = newY;

    // Update movement state
    const speed = Math.sqrt(movement.velocityX ** 2 + movement.velocityY ** 2);
    movement.isMoving = speed > 1; // Small threshold to avoid floating point issues

    // Check for trigger tiles after movement
    if (moved && this.triggerCheck) {
      this.triggerCheck(transform.x, transform.y, entity.id);
    }

    // Emit event if player moved
    if (moved && entity.hasTag('player')) {
      EventBus.emitEvent('player:moved', {
        x: transform.x,
        y: transform.y,
      });
    }
  }

  /**
   * Check if a position is within map bounds
   */
  public isInBounds(x: number, y: number): boolean {
    return (
      x >= this.mapBounds.minX &&
      x <= this.mapBounds.maxX &&
      y >= this.mapBounds.minY &&
      y <= this.mapBounds.maxY
    );
  }

  public destroy(): void {
    // No cleanup needed
  }
}
